import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Users, UserCircle } from 'lucide-react';
import { useMosque } from '../contexts/MosqueContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PengurusPage = () => {
  const { mosqueId } = useMosque();
  const [pengurus, setPengurus] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (mosqueId) {
      axios.get(`${API_URL}/api/pengurus`, { params: { mosque_id: mosqueId } })
        .then(r => setPengurus(r.data || []))
        .catch(() => setPengurus([]))
        .finally(() => setLoading(false));
    }
  }, [mosqueId]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-[#F8F9FA] pb-24"
    >
      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-800 to-emerald-950 px-6 pt-16 pb-10 text-white">
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center mb-4">
            <Users className="w-7 h-7 text-amber-300" />
          </div>
          <h1 className="text-4xl font-heading font-bold tracking-tight mb-2">Pengurus Masjid</h1>
          <p className="text-white/70 text-sm font-body">Susunan pengurus & takmir yang diamanahi</p>
        </motion.div>
      </div>

      <div className="px-6 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-emerald-200 border-t-emerald-700" />
          </div>
        ) : pengurus.length === 0 ? (
          <div className="text-center py-20">
            <Users className="w-16 h-16 text-stone-200 mx-auto mb-4" />
            <p className="text-stone-500 font-body text-lg">Belum ada data pengurus</p>
            <p className="text-stone-400 text-sm mt-1">Admin masjid dapat menambahkan susunan pengurus melalui dasbor</p>
          </div>
        ) : (
          <>
            <p className="text-xs text-stone-400 font-body mb-6 text-center">
              {pengurus.length} Pengurus Terdaftar
            </p>
            <div className="grid grid-cols-2 gap-4">
              {pengurus.map((p, i) => (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.06 * i }}
                  className="bg-white rounded-3xl border border-stone-100 shadow-sm overflow-hidden flex flex-col items-center p-4 gap-3"
                  data-testid={`pengurus-card-${p.id}`}
                >
                  {/* Foto */}
                  <div className="w-20 h-20 rounded-full overflow-hidden bg-emerald-50 border-2 border-emerald-100 flex items-center justify-center flex-shrink-0">
                    {p.foto_url ? (
                      <img
                        src={p.foto_url}
                        alt={p.nama}
                        className="w-full h-full object-cover"
                        onError={e => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <UserCircle className="w-10 h-10 text-emerald-300" />
                    )}
                  </div>

                  {/* Info */}
                  <div className="text-center w-full">
                    <p className="font-body font-bold text-primary-dark text-sm leading-tight line-clamp-2">{p.nama}</p>
                    <div className="mt-1.5">
                      <span className="inline-block text-xs font-medium bg-emerald-50 text-emerald-800 rounded-full px-3 py-0.5 border border-emerald-100">
                        {p.jabatan}
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Decorative note */}
            <div className="mt-8 text-center">
              <p className="text-xs text-stone-400 font-body">
                Semoga Allah SWT senantiasa memudahkan amanah para pengurus masjid
              </p>
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
};

export default PengurusPage;
